import { useDealership } from '@/contexts/DealershipContext'
import { createDealershipGroup } from '@/services/dealership.api'
import { getApiErrorMessage } from '@/utils/apiError'
import { buildGroupConfigurationPayload } from '@/utils/groupConfigurationPayload'
import { Box, Button, Card, ColorInput, Group, Stack, Text, TextInput, Textarea, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconBuilding, IconCheck, IconMail, IconMapPin, IconPhone, IconX } from '@tabler/icons-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

/**
 * Format phone number to (XXX) XXX-XXXX format
 */
const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '')

  // Limit to 10 digits
  const limitedDigits = digits.slice(0, 10)

  // Format based on length
  if (limitedDigits.length === 0) return ''
  if (limitedDigits.length <= 3) return `(${limitedDigits}`
  if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`
  return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`
}

interface AddDealershipGroupFormValues {
  groupId: string
  groupName: string
  groupDescription?: string
  headquarters?: {
    address?: {
      line1?: string
      line2?: string
      city?: string
      state?: string
      zipCode?: string
    }
    primaryPhone?: string
    email?: string
    website?: string
  }
  brandingGuidelines?: {
    logoUrl?: string
    primaryColor?: string
    secondaryColor?: string
  }
  timezone?: string
}

export function AddDealershipGroup() {
  const navigate = useNavigate()
  const { refreshData } = useDealership()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<AddDealershipGroupFormValues>({
    initialValues: {
      groupId: '',
      groupName: '',
      groupDescription: '',
      headquarters: {
        address: {
          line1: '',
          line2: '',
          city: '',
          state: '',
          zipCode: '',
        },
        primaryPhone: '',
        email: '',
        website: '',
      },
      brandingGuidelines: {
        logoUrl: '',
        primaryColor: '#003087',
        secondaryColor: '#FFB81C',
      },
      timezone: 'America/Chicago',
    },
    validate: {
      groupId: value => {
        if (!value?.trim()) return 'Group ID is required'
        // Validate format: 3-5 character alphanumeric
        if (!/^[A-Z0-9]{3,5}$/i.test(value.trim())) {
          return 'Group ID must be a 3-5 character alphanumeric value'
        }
        return null
      },
      groupName: value => (value?.trim() ? null : 'Group name is required'),
      headquarters: {
        email: value => {
          if (!value) return null
          return /^\S+@\S+\.\S+$/.test(value) ? null : 'Invalid email address'
        },
      },
      brandingGuidelines: {
        primaryColor: value => {
          if (!value) return null
          return /^#[0-9A-Fa-f]{6}$/.test(value) ? null : 'Must be 6-digit hex color (e.g., #FFFFFF)'
        },
        secondaryColor: value => {
          if (!value) return null
          return /^#[0-9A-Fa-f]{6}$/.test(value) ? null : 'Must be 6-digit hex color (e.g., #FFFFFF)'
        },
      },
    },
  })

  const handleSubmit = async (values: AddDealershipGroupFormValues) => {
    setIsSubmitting(true)

    try {
      const { groupId, ...formConfig } = values
      const configurationData = buildGroupConfigurationPayload(formConfig)

      const response = await createDealershipGroup({
        groupId,
        configurationData,
      })

      // Show success notification
      notifications.show({
        title: 'Dealership Group Created',
        message: `${values.groupName} has been successfully created`,
        color: 'green',
        icon: <IconCheck size={18} />,
      })

      // Refresh the dealership groups list to include the newly created group
      await refreshData()

      const createdGroupId = response.groupId || groupId

      // Navigate with state so GroupConfiguration loads the new group regardless of context update timing
      navigate('/group/configuration', { state: { groupId: createdGroupId } })
    } catch (error: any) {
      const errorMessage = getApiErrorMessage(error, 'Failed to create dealership group. Please try again.')

      notifications.show({
        title: 'Error Creating Group',
        message: errorMessage,
        color: 'red',
        icon: <IconX size={18} />,
        autoClose: 10000,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box p="xl">
      <Stack gap="md" style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <Group>
          <Button variant="subtle" leftSection={<IconArrowLeft size={18} />} onClick={() => navigate('/admin/system')}>
            Back to System Administration
          </Button>
        </Group>

        <Box>
          <Group gap="xs" align="center" mb={4}>
            <IconBuilding size={22} stroke={1.5} />
            <Title order={2}>Add Dealership Group</Title>
          </Group>
          <Text size="sm" c="dimmed">
            Create a new dealership group with basic information and branding guidelines
          </Text>
        </Box>

        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="xl">
            {/* Business Basics Section */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Text size="lg" fw={600} mb="xs">
                    Group Information
                  </Text>
                  <Text size="sm" c="dimmed">
                    Enter basic information about the dealership group
                  </Text>
                </Box>

                <Stack gap="md">
                  <Group grow>
                    <TextInput
                      label="Group ID"
                      placeholder="D2233"
                      required
                      maxLength={5}
                      description="3-5 character alphanumeric value (e.g., D2233, 4675)"
                      value={form.values.groupId || ''}
                      onChange={e => {
                        const value = e.target.value
                          .replace(/[^A-Z0-9]/gi, '')
                          .slice(0, 5)
                          .toUpperCase()
                        form.setFieldValue('groupId', value)
                      }}
                      onBlur={() => form.validateField('groupId')}
                      error={form.errors.groupId}
                    />
                    <TextInput
                      label="Group Name"
                      placeholder="Enter dealership group name"
                      required
                      leftSection={<IconBuilding size={16} />}
                      {...form.getInputProps('groupName')}
                    />
                  </Group>

                  <Textarea
                    label="Description"
                    placeholder="Brief description of the dealership group"
                    rows={3}
                    {...form.getInputProps('groupDescription')}
                  />
                </Stack>
              </Stack>
            </Card>

            {/* Headquarters Address Section */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Text size="lg" fw={600} mb="xs">
                    Headquarters Address
                  </Text>
                  <Text size="sm" c="dimmed">
                    Enter the headquarters address for the dealership group
                  </Text>
                </Box>

                <Stack gap="md">
                  <TextInput
                    label="Address Line 1"
                    placeholder="Street address"
                    leftSection={<IconMapPin size={16} />}
                    {...form.getInputProps('headquarters.address.line1')}
                  />

                  <TextInput
                    label="Address Line 2"
                    placeholder="Suite, unit, building, floor, etc."
                    {...form.getInputProps('headquarters.address.line2')}
                  />

                  <Group grow>
                    <TextInput label="City" placeholder="City" {...form.getInputProps('headquarters.address.city')} />
                    <TextInput
                      label="State"
                      placeholder="State"
                      maxLength={2}
                      {...form.getInputProps('headquarters.address.state')}
                    />
                    <TextInput
                      label="ZIP Code"
                      placeholder="ZIP"
                      maxLength={10}
                      {...form.getInputProps('headquarters.address.zipCode')}
                    />
                  </Group>
                </Stack>
              </Stack>
            </Card>

            {/* Contact Information Section */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Text size="lg" fw={600} mb="xs">
                    Contact Information
                  </Text>
                  <Text size="sm" c="dimmed">
                    Enter contact details for the dealership group
                  </Text>
                </Box>

                <Stack gap="md">
                  <TextInput
                    label="Primary Phone"
                    placeholder="(555) 123-4567"
                    leftSection={<IconPhone size={16} />}
                    maxLength={14}
                    value={form.values.headquarters?.primaryPhone || ''}
                    onChange={e => {
                      const formatted = formatPhoneNumber(e.target.value)
                      form.setFieldValue('headquarters.primaryPhone', formatted)
                    }}
                    onBlur={() => form.validateField('headquarters.primaryPhone')}
                    error={(form.errors.headquarters as any)?.primaryPhone}
                  />

                  <TextInput
                    label="Email"
                    type="email"
                    placeholder="contact@dealershipgroup.com"
                    leftSection={<IconMail size={16} />}
                    {...form.getInputProps('headquarters.email')}
                  />

                  <TextInput
                    label="Website"
                    type="url"
                    placeholder="https://www.dealershipgroup.com"
                    {...form.getInputProps('headquarters.website')}
                  />
                </Stack>
              </Stack>
            </Card>

            {/* Branding Guidelines Section */}
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Stack gap="md">
                <Box>
                  <Text size="lg" fw={600} mb="xs">
                    Branding Guidelines
                  </Text>
                  <Text size="sm" c="dimmed">
                    Configure branding colors and logo for the dealership group
                  </Text>
                </Box>

                <Stack gap="md">
                  <TextInput
                    label="Logo URL"
                    placeholder="https://example.com/logo.png"
                    {...form.getInputProps('brandingGuidelines.logoUrl')}
                  />

                  <Group grow>
                    <ColorInput
                      label="Primary Brand Color"
                      placeholder="Pick a color"
                      format="hex"
                      {...form.getInputProps('brandingGuidelines.primaryColor')}
                    />

                    <ColorInput
                      label="Secondary Brand Color"
                      placeholder="Pick a color"
                      format="hex"
                      {...form.getInputProps('brandingGuidelines.secondaryColor')}
                    />
                  </Group>
                </Stack>
              </Stack>
            </Card>

            {/* Actions */}
            <Group justify="flex-end">
              <Button variant="default" onClick={() => navigate('/admin/system')} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting} leftSection={<IconCheck size={18} />}>
                Create Dealership Group
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
    </Box>
  )
}
